/**
 * Ship switching at a station (M6 spec §4 "switch to any owned ship docked at the same station"; plan P24, §9 "the
 * flown ship is not a worker agent").
 *
 * `boardShip` is the Career command: it swaps `career.ship` with a hangar entry.
 * - **Leaving** writes the flown ship back into the hangar: a fleet ship's entry gets its loadout, salvaged
 *   provenance, hull and torpedoes (insurance, escape pod and a claimed hull's flaws and value kept, M9 CH-8); the
 *   Career's own lease ship becomes the reserved `OWN_SHIP_ID` entry, parked at the station (`stationId`). In the
 *   worker the left fleet ship rejoins the pool as a docked agent on its standing order (`leaveShip`/`boardShip`
 *   there; a new ship's order is `hold`).
 * - **Boarding** puts the entry's loadout into `career.ship` and sets `career.flownShip` (absent for the lease ship,
 *   whose entry leaves the hangar). The worker's agent leaves the pool.
 * - **Salvaged provenance moves with the ship** (economy plan M6I-5): `ownedFromShip` copies `ShipState.salvaged`,
 *   `loadoutOf` restores it; a salvaged module never becomes a plain `fitted`/`locker` entry, so it still resells at
 *   component value (`sellSalvaged`), never at `sellFraction` × the module price.
 * - **A claimed hull's full hull is its flawed one** (M9 CH-12b): a hull flaw (or a First Rush edge on `hull`) scales
 *   the def's full hull (`flawedFullHull`, CH-4's `applyHulkTraits`), so boarding a flawed ship with no recorded
 *   `hull` gives it the flawed full hull, never the pristine one, and leaving it at that hull records no damage.
 * - **The hold moves with the pilot** (D-402: it is a transfer between docked ships at one station): the new ship
 *   must hold it (`cargoFull`), and the hold keeps contract and story cargo with the player. Hull and torpedoes stay
 *   with each ship. The fit is checked against the power budget (`powerBudget`), salvaged modules included.
 *
 * Pure: the input career is never changed.
 */
import type { M2Data } from '../data/m2Schemas'
import type { ShipLimits } from '../career/services'
import { powerBudget, powerDraw } from '../career/fitting'
import { holdCapacityT } from '../career/hullFit'
import { EPSILON, cargoUsedOnT, emptyWares } from '../career/ops'
import { fail, formatTonnes, ok, type CmdResult } from '../career/result'
import type { CargoState, Career, OwnedShip, ParkedL, ShipState } from '../career/types'
import { HULK_STATS, type HulkStat } from '../hulks/data'
import { applyHulkTraits } from '../hulks/flaws'
import { OWN_SHIP_ID, flownEntry, hangarOf, loadoutOf, ownedFromShip, ownedShip, recordHullEvent } from './hangar'

/** Full hull and torpedo rounds of a ship def (`ships.json`), or null for an unknown def. */
export type ShipLimitsOf = (defId: string) => ShipLimits | null

export interface BoardShipArgs {
  /** The hangar entry to board: a fleet id, or `OWN_SHIP_ID` for the Career's own lease ship. */
  target: string
  /** The strategic station the player is docked at. */
  stationId: string
  /** The strategic station the target fleet ship is docked at (the worker's view); ignored for the lease ship. */
  targetStationId: string | null
  /** The worker's hp share of the target fleet ship (0..1, default 1). */
  targetHp?: number
  limitsOf: ShipLimitsOf
}

/** The full hull of a def whose pristine full hull is `pristine`, with the hull's `flaws` bitmask applied (0 → pristine). */
export function flawedFullHull(pristine: number, flaws: number | undefined): number {
  if (flaws === undefined || flaws === 0) return pristine
  const stats = Object.fromEntries(HULK_STATS.map((k) => [k, k === 'hull' ? pristine : 0])) as Record<HulkStat, number>
  return applyHulkTraits(stats, flaws).hull
}

/** The fleet id the player flies now (`OWN_SHIP_ID` for the lease ship). */
export function flownShipId(career: Career): string {
  return career.flownShip ?? OWN_SHIP_ID
}

/** The hangar entry the flown ship leaves behind (null when a flown fleet ship has no entry any more). */
export function leftEntry(career: Career, stationId: string, limitsOf: ShipLimitsOf): OwnedShip | null {
  const id = flownShipId(career)
  const prev = id === OWN_SHIP_ID ? null : ownedShip(career, id)
  if (id !== OWN_SHIP_ID && prev === null) return null
  const ship = career.ship
  const out = ownedFromShip(ship, id, prev?.insured ?? false, prev?.escapePod ?? false, prev)
  const full = limitsOf(ship.defId)
  const fullHull = full === null ? 0 : flawedFullHull(full.hull, prev?.flaws)
  if (full === null || ship.hull < fullHull - EPSILON) out.hull = Math.max(0, ship.hull)
  if (full === null || ship.torpedoes < full.torpedoRounds) out.torpedoes = Math.max(0, Math.floor(ship.torpedoes))
  if (id === OWN_SHIP_ID) {
    out.stationId = stationId
    out.dockedAt = career.lastDockedStation
  }
  return out
}

/** The hangar with `entry` written in (replacing the entry of its fleet id, or appended). */
function withEntry(hangar: readonly OwnedShip[], entry: OwnedShip): OwnedShip[] {
  const i = hangar.findIndex((s) => s.fleetId === entry.fleetId)
  if (i < 0) return [...hangar, entry]
  const next = hangar.slice()
  next[i] = entry
  return next
}

/**
 * Boards hangar ship `target` docked at the player's station: the flown ship is written back to the hangar and the
 * entry becomes `career.ship`. Refusals: the ship flown now or one not in the hangar (`invalidAmount`), another
 * station (`serviceUnavailable`), an unknown ship def (`invalidAmount`), a fit over its power budget
 * (`powerBudget`), a hold the new ship can't carry (`cargoFull`).
 */
export function boardShip(career: Career, data: Pick<M2Data, 'modules'>, args: BoardShipArgs): CmdResult<Career> {
  const { target, stationId, limitsOf } = args
  if (target === flownShipId(career)) return fail('invalidAmount', 'You are flying that ship already.', { ship: target })
  const entry = ownedShip(career, target)
  if (entry === null) return fail('invalidAmount', `Ship ${target} is not in your hangar.`, { ship: target })
  // M10 P5a11a: a ship riding in a cradle is boarded through its carrier (`boardCarrier`, `launchCarried`), never from a station.
  if (cradleOf(career, target) !== null) return fail('serviceUnavailable', 'That ship rides in a cradle.', { ship: target })
  const at =target === OWN_SHIP_ID ? (entry.stationId ?? null) : args.targetStationId
  if (at !== stationId) return fail('serviceUnavailable', 'That ship is not docked at this station.', { ship: target, station: at ?? '' })
  const full = limitsOf(entry.defId)
  if (full === null) return fail('invalidAmount', `Unknown ship def ${entry.defId}.`, { ship: target })

  const loadout = loadoutOf(entry)
  const draw = powerDraw(loadout.fitted, data)
  const budget = powerBudget(loadout.fitted, data)
  if (draw > budget) return fail('powerBudget', `Not enough power: the fit draws ${draw} of a ${budget} budget.`, { draw, budget })
  const capacityT = holdCapacityT(loadout.defId, loadout.fitted, data)
  // M10 P5a6b H1: weigh the hold by the target hull's class multipliers, not the flown hull's.
  const used = cargoUsedOnT(career, loadout.defId)
  if (used > capacityT + EPSILON) {
    return fail('cargoFull', `Your hold carries ${formatTonnes(used)} t but that ship holds only ${formatTonnes(capacityT)} t.`, {
      needed: used,
      capacity: capacityT,
    })
  }

  const hp = target === OWN_SHIP_ID ? 1 : Math.min(1, Math.max(0, args.targetHp ?? 1))
  const fullHull = flawedFullHull(full.hull, entry.flaws)
  const ship: ShipState = {
    defId: loadout.defId,
    hull: Math.min(entry.hull ?? fullHull, hp * fullHull),
    torpedoes: entry.torpedoes ?? full.torpedoRounds,
    fitted: loadout.fitted,
    locker: loadout.locker,
  }
  if (loadout.salvaged !== undefined) ship.salvaged = loadout.salvaged
  if (loadout.resonanceMeter === true) ship.resonanceMeter = true

  let hangar = career.hangar ?? []
  const left = leftEntry(career, stationId, limitsOf)
  if (left !== null) hangar = withEntry(hangar, left)
  if (target === OWN_SHIP_ID) hangar = hangar.filter((s) => s.fleetId !== OWN_SHIP_ID)
  const next: Career = { ...career, ship, cargo: { ...career.cargo, capacityT }, hangar }
  if (target === OWN_SHIP_ID) delete next.flownShip
  else next.flownShip = target
  return ok(next)
}

// ---------------------------------------------------------------------------------------------------------------
// M10 P5a11a (plan 2026-10-17-m10-p5a card P5a11a, Q1; D-1073, D-1076, D-1086): recovery and switching through the
// cradle. The carrier pair (an L and the S in its `DOCK_CRADLE`) replaces the same-station rule, and each ship of
// the pair keeps its own hold (Q1, `OwnedShip.hold`): the live hold (`career.cargo`) is the flown ship's, the
// other's waits on its hangar entry. "Unload to the <L>" is the one way goods cross (free, instant, all or nothing).
// The states: flying the S with the L parked (`Career.parkedL`); flying the S docked back in the L's cradle
// (`Career.carriedBy` = the L, `parkedL` cleared, the L's entry `cradle.carried` = the S); flying the L with the S
// in its cradle (`carriedBy` absent, the L's entry `cradle.carried` = the S). Pure: the input career is never changed.
// ---------------------------------------------------------------------------------------------------------------

/** The fleet id of the L whose cradle holds `fleetId` (the hangar's `cradle.carried`), or null. */
export function cradleOf(career: Career, fleetId: string): string | null {
  for (const e of hangarOf(career)) if (e.cradle?.carried === fleetId) return e.fleetId
  return null
}

/** An empty hold of `capacityT` tonnes. */
function emptyHold(capacityT: number): CargoState {
  return { capacityT, wares: emptyWares(), components: [] }
}

/** Adds `b`'s per-key quantities into a copy of `a` (absent keys count 0). */
function addCounts<K extends string>(a: Partial<Record<K, number>>, b: Partial<Record<K, number>>): Partial<Record<K, number>> {
  const out: Partial<Record<K, number>> = { ...a }
  for (const k of Object.keys(b) as K[]) out[k] = (out[k] ?? 0) + (b[k] ?? 0)
  return out
}

/** `a` with `b`'s goods added (wares, the goods block, components, processed tonnes); `a`'s capacity kept. */
export function mergeHolds(a: Readonly<CargoState>, b: Readonly<CargoState>): CargoState {
  const wares = { ...a.wares }
  for (const w of Object.keys(b.wares) as (keyof typeof wares)[]) wares[w] = (wares[w] ?? 0) + b.wares[w]
  const out: CargoState = { capacityT: a.capacityT, wares, components: [...a.components, ...b.components] }
  if (a.parts !== undefined || b.parts !== undefined) out.parts = addCounts(a.parts ?? {}, b.parts ?? {})
  if (a.processed !== undefined || b.processed !== undefined) out.processed = addCounts(a.processed ?? {}, b.processed ?? {})
  return out
}

/**
 * The flown ship becomes hangar ship `target` across the cradle pair: the flown ship's entry keeps the live hold as
 * its own `hold` (Q1), and `target`'s `hold` becomes the live hold (empty when it has none). No station rule (the
 * pair replaces it) and no `cargoFull` (no goods move). Refusals as `boardShip`: an unknown def, the power budget.
 */
function switchAcrossPair(career: Career, data: Pick<M2Data, 'modules'>, target: string, limitsOf: ShipLimitsOf): CmdResult<Career> {
  const entry = ownedShip(career, target)
  if (entry === null) return fail('invalidAmount', `Ship ${target} is not in your hangar.`, { ship: target })
  const full = limitsOf(entry.defId)
  if (full === null) return fail('invalidAmount', `Unknown ship def ${entry.defId}.`, { ship: target })
  const loadout = loadoutOf(entry)
  const draw = powerDraw(loadout.fitted, data)
  const budget = powerBudget(loadout.fitted, data)
  if (draw > budget) return fail('powerBudget', `Not enough power: the fit draws ${draw} of a ${budget} budget.`, { draw, budget })
  const capacityT = holdCapacityT(loadout.defId, loadout.fitted, data)
  const fullHull = flawedFullHull(full.hull, entry.flaws)
  const ship: ShipState = {
    defId: loadout.defId,
    hull: Math.min(entry.hull ?? fullHull, fullHull),
    torpedoes: entry.torpedoes ?? full.torpedoRounds,
    fitted: loadout.fitted,
    locker: loadout.locker,
  }
  if (loadout.salvaged !== undefined) ship.salvaged = loadout.salvaged
  if (loadout.resonanceMeter === true) ship.resonanceMeter = true

  let hangar = career.hangar ?? []
  const prev = ownedShip(career, flownShipId(career))
  const left = leftEntry(career, '', limitsOf)
  if (left !== null) {
    // The left ship stays in the pair, not at a station: no `stationId`, so no station can board it.
    delete left.stationId
    // Its P5a0 keys stay with it (`leftEntry` writes the loadout and condition only).
    if (prev?.cradle !== undefined) left.cradle = { ...prev.cradle }
    if (prev?.name !== undefined) left.name = prev.name
    if (prev?.history !== undefined) left.history = [...prev.history]
    left.hold = { ...career.cargo }
    hangar = withEntry(hangar, left)
  }
  if (target === OWN_SHIP_ID) hangar = hangar.filter((s) => s.fleetId !== OWN_SHIP_ID)
  else {
    const boarded: OwnedShip = { ...entry }
    delete boarded.hold
    hangar = withEntry(hangar, boarded)
  }
  const cargo = entry.hold === undefined ? emptyHold(capacityT) : { ...entry.hold, capacityT }
  const next: Career = { ...career, ship, cargo, hangar }
  if (target === OWN_SHIP_ID) delete next.flownShip
  else next.flownShip = target
  return ok(next)
}

/**
 * The S docks back into its parked L's cradle (P5a9b's flow arrived): `carriedBy` = the L, `parkedL` cleared, the
 * L's entry carries the S. Refused unless `carrierId` is the parked L. A repeat is a no-op.
 */
export function dockBackToCarrier(career: Career, carrierId: string): CmdResult<Career> {
  if (career.carriedBy === carrierId && career.parkedL === undefined) return ok(career)
  if (career.parkedL?.fleetId !== carrierId) return fail('serviceUnavailable', 'That ship is not your parked carrier.', { ship: carrierId })
  const l = ownedShip(career, carrierId)
  if (l === null) return fail('invalidAmount', `Ship ${carrierId} is not in your hangar.`, { ship: carrierId })
  const next: Career = { ...career, carriedBy: carrierId, hangar: withEntry(career.hangar ?? [], { ...l, cradle: { carried: flownShipId(career) } }) }
  delete next.parkedL
  return ok(next)
}

/**
 * The flown S launches from the cradle it docked back into (no helm switch): the L parks again at `parked` (its live
 * pose, `parkedL.ts parkedLRecord`), `carriedBy` cleared, the cradle empty.
 */
export function relaunchFromCarrier(career: Career, parked: Readonly<ParkedL>): CmdResult<Career> {
  const lId = career.carriedBy
  if (lId === undefined || parked.fleetId !== lId) return fail('serviceUnavailable', 'You are not in a cradle.', {})
  const l = ownedShip(career, lId)
  if (l === null) return fail('invalidAmount', `Ship ${lId} is not in your hangar.`, { ship: lId })
  const next: Career = { ...career, parkedL: { ...parked }, hangar: withEntry(career.hangar ?? [], { ...l, cradle: { carried: null } }) }
  delete next.carriedBy
  return ok(next)
}

/**
 * **Unload to the <L>** (Q1): the flown S, carried in its L's cradle, moves its whole hold into the L's `hold`.
 * Refused `cargoFull` when the L can't take it all (nothing moves). The S's hold is then empty.
 */
export function unloadToCarrier(career: Career, data: Pick<M2Data, 'modules'>): CmdResult<Career> {
  const lId = career.carriedBy
  if (lId === undefined) return fail('serviceUnavailable', 'You are not in a cradle.', {})
  const l = ownedShip(career, lId)
  if (l === null) return fail('invalidAmount', `Ship ${lId} is not in your hangar.`, { ship: lId })
  const capacityT = holdCapacityT(l.defId, l.fitted, data)
  const hold = mergeHolds(l.hold ?? emptyHold(capacityT), career.cargo)
  hold.capacityT = capacityT
  const used = cargoUsedOnT({ ...career, cargo: hold }, l.defId)
  if (used > capacityT + EPSILON) {
    return fail('cargoFull', `That would carry ${formatTonnes(used)} t but the ship holds only ${formatTonnes(capacityT)} t.`, { needed: used, capacity: capacityT })
  }
  return ok({ ...career, cargo: emptyHold(career.cargo.capacityT), hangar: withEntry(career.hangar ?? [], { ...l, hold }) })
}

/**
 * **Take the helm** (a `boardShip` for the carrier pair): from the S carried in its L's cradle, the player flies the
 * L. The S's remaining hold stays on its entry (Q1), the L's hold becomes the live one; the S stays in the cradle.
 * `day` (the faction day, -1 = unknown) writes the L's 'boarded' hull event (P5a3b).
 */
export function boardCarrier(career: Career, data: Pick<M2Data, 'modules'>, limitsOf: ShipLimitsOf, day = -1): CmdResult<Career> {
  const lId = career.carriedBy
  if (lId === undefined) return fail('serviceUnavailable', 'You are not in a cradle.', {})
  const r = switchAcrossPair(career, data, lId, limitsOf)
  if (!r.ok) return r
  const next = r.value
  delete next.carriedBy
  return ok(day < 0 ? next : recordHullEvent(next, lId, { kind: 'boarded', day }))
}

/**
 * Launching again from the L's helm: the S in the flown L's cradle becomes the flown ship (its own hold back), the L
 * parks at `parked` (its live pose) with its hold on its entry and the cradle empty. Refused when the flown ship
 * carries no S, or `parked` names another ship.
 */
export function launchCarried(career: Career, data: Pick<M2Data, 'modules'>, limitsOf: ShipLimitsOf, parked: Readonly<ParkedL>): CmdResult<Career> {
  const l = flownEntry(career)
  const sId = l?.cradle?.carried ?? null
  if (l === null || sId === null || parked.fleetId !== l.fleetId) return fail('serviceUnavailable', 'No ship rides in your cradle.', {})
  const r = switchAcrossPair({ ...career, hangar: withEntry(career.hangar ?? [], { ...l, cradle: { carried: null } }) }, data, sId, limitsOf)
  if (!r.ok) return r
  const next = r.value
  next.parkedL = { ...parked }
  delete next.carriedBy
  return ok(next)
}

/**
 * Stowing fleet S `shipId` into the flown L's empty cradle at a station (W2 review: the main thread checks both are
 * at the same station; the worker's `dockToCarrier` doesn't). Refused unless the S is docked at `stationId`, where the
 * player is (`serviceUnavailable`), and the cradle is free. The L's entry carries the S.
 */
export function stowInCarrier(career: Career, shipId: string, stationId: string, shipStationId: string | null): CmdResult<Career> {
  const l = flownEntry(career)
  if (l === null || l.cradle === undefined) return fail('serviceUnavailable', 'Your ship has no cradle.', {})
  if (l.cradle.carried !== null) return fail('serviceUnavailable', 'Your cradle is in use.', { ship: l.cradle.carried })
  if (shipId === l.fleetId || ownedShip(career, shipId) === null) return fail('invalidAmount', `Ship ${shipId} is not in your hangar.`, { ship: shipId })
  if (shipStationId !== stationId) return fail('serviceUnavailable', 'That ship is not docked at this station.', { ship: shipId, station: shipStationId ?? '' })
  return ok({ ...career, hangar: withEntry(career.hangar ?? [], { ...l, cradle: { carried: shipId } }) })
}
